import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api, extractError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, HeartHandshake, GraduationCap, ArrowRight, AlertTriangle } from "lucide-react";

export default function InviteAccept() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) return;
    api.get(`/invites/${encodeURIComponent(token)}`)
      .then(({ data }) => setInvite(data))
      .catch((e) => setError(extractError(e)));
  }, [token]);

  const accept = () => {
    const p = new URLSearchParams();
    p.set("role", invite.role);
    if (invite.recipient_email) p.set("email", invite.recipient_email);
    if (invite.recipient_name) p.set("name", invite.recipient_name);
    p.set("invite", token);
    navigate(`/register?${p.toString()}`);
  };

  if (error) {
    return (
      <div className="min-h-screen bg-stone2-50">
        <Navbar />
        <div className="max-w-md mx-auto px-4 py-14">
          <div className="paper-card p-8 text-center" data-testid="invite-error">
            <AlertTriangle className="w-10 h-10 text-danger mx-auto" />
            <h1 className="mt-3 font-heading font-bold text-sage-900 text-xl">Invite unavailable</h1>
            <p className="mt-2 text-sm text-stone2-600">{error}</p>
            <button onClick={() => navigate("/register")}
              className="btn-primary mt-6 w-full justify-center"
              data-testid="invite-fallback-register">
              Sign up on your own
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!invite) {
    return (
      <div className="min-h-screen bg-stone2-50">
        <Navbar />
        <div className="max-w-md mx-auto px-4 py-14">
          <div className="paper-card p-10 text-center">
            <Loader2 className="w-6 h-6 animate-spin mx-auto text-sage-600" />
            <p className="mt-3 text-sm text-stone2-600">Loading your invite…</p>
          </div>
        </div>
      </div>
    );
  }

  const roleLabel = invite.role === "sitter" ? "Specialized Babysitter" : "Family";
  const roleIcon = invite.role === "sitter" ? <GraduationCap className="w-5 h-5" /> : <HeartHandshake className="w-5 h-5" />;

  return (
    <div className="min-h-screen bg-stone2-50">
      <Navbar />
      <div className="max-w-lg mx-auto px-4 py-14" data-testid="invite-accept-page">
        <span className="section-eyebrow">You're invited</span>
        <h1 className="mt-2 text-3xl font-heading font-bold text-sage-900">
          Welcome to AutismBabysitters{invite.recipient_name ? `, ${invite.recipient_name.split(" ")[0]}` : ""}.
        </h1>
        <p className="mt-3 text-stone2-600 leading-relaxed">
          You've been personally invited to join as a <strong>{roleLabel}</strong>.
        </p>

        {invite.message && (
          <blockquote className="mt-6 paper-card p-5 border-l-4 border-ochre-500">
            <p className="text-sm text-sage-900 italic leading-relaxed">"{invite.message}"</p>
            {invite.sender_email && (
              <p className="mt-3 text-xs text-stone2-500">— {invite.sender_email}</p>
            )}
          </blockquote>
        )}

        <div className="mt-6 paper-card p-5">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-sage-700">
            {roleIcon} Your role
          </div>
          <div className="mt-1 font-heading font-semibold text-sage-900 text-lg">{roleLabel}</div>
          {invite.recipient_email && (
            <div className="mt-3 text-sm text-stone2-600">
              We'll pre-fill your account with <strong className="text-sage-900">{invite.recipient_email}</strong>
            </div>
          )}
        </div>

        <button onClick={accept} className="btn-primary w-full justify-center mt-6" data-testid="invite-accept-btn">
          Accept & create my account <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
